import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Tag } from 'lucide-react';
import { getServiceById } from '../../data/services';
import settings from '../../constants/settings';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import '../../styles/serviceDetail.css';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [markdownContent, setMarkdownContent] = useState('');

  useEffect(() => {
    const fetchService = async () => {
      try {
        setLoading(true);
        const serviceData = await getServiceById(serviceId);
        if (serviceData) {
          setService(serviceData);
          
          // Extract markdown content (remove frontmatter)
          const content = serviceData.content || '';
          const frontmatterEnd = content.indexOf('---', 3) + 3;
          let markdownBody = content.substring(frontmatterEnd).trim();
          
          // Remove the first H1 heading (title) as we already display it
          const firstHeadingMatch = markdownBody.match(/^# .+$/m);
          if (firstHeadingMatch && firstHeadingMatch.index === 0) {
            const firstHeadingEndIndex = markdownBody.indexOf('\n', firstHeadingMatch.index);
            if (firstHeadingEndIndex !== -1) {
              markdownBody = markdownBody.substring(firstHeadingEndIndex + 1).trim();
            }
          }
          
          setMarkdownContent(markdownBody);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error fetching service:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchService();
  }, [serviceId]);

  // Function to handle order button click
  const handleOrderClick = (e) => {
    e.preventDefault();
    // Get the consultant number from settings and create the WhatsApp link
    var consultantNumber = settings.wa_consultant_link.replace('https://wa.me/', '');
    if (serviceId === '14') {
      consultantNumber = "6285179553070";
    }
    const message = `Assalamu'alaikum. Saya ingin ikut ${service?.title || 'Layanan'}`;
    window.open(`https://wa.me/${consultantNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  // If loading, show loading state
  if (loading) {
    return (
      <div className="pt-[90px] bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat detail layanan...</p>
        </div>
      </div>
    );
  }

  // If error or service not found, show error state
  if (error || !service) {
    return (
      <div className="pt-[90px] bg-white">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link to="/services" className="inline-flex items-center text-gold hover:text-cream transition-colors mb-8">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Kembali ke Semua Layanan
          </Link>
          
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <h1 className="text-3xl font-serif font-bold text-gray-900 mb-4">Layanan Tidak Ditemukan</h1>
            <p className="text-gray-600 mb-6">Maaf, layanan yang Anda cari tidak tersedia atau telah dihapus.</p>
            <Link 
              to="/services" 
              className="inline-flex items-center justify-center bg-gold hover:bg-dark-navy text-white font-medium rounded-md px-6 py-3 transition-colors"
            >
              Lihat Semua Layanan
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-[90px] bg-white">
      <Helmet>
        <title>{service.title} | Pintar Pilih Kuliah</title>
        <meta name="description" content={service.description} />
      </Helmet>
      
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/services" className="inline-flex items-center text-gold hover:text-cream transition-colors mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Kembali ke Semua Layanan
        </Link>
        
        <article className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Service Image */}
          <div className="aspect-[16/9] overflow-hidden bg-gray-100">
            <img
              src={service.image}
              alt={service.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = '/images/services/placeholder.svg';
              }}
            />
          </div>
          
          <div className="p-6 md:p-8">
            {/* Header */}
            <header className="mb-8">
              <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 leading-tight">{service.title}</h1>
                <span className="inline-flex items-center text-sm font-medium bg-cream/30 text-dark-navy px-3 py-1 rounded-full">
                  <Tag className="w-3 h-3 mr-1" />
                  {service.category}
                </span>
              </div>
              
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Biaya Layanan</h2>
                <p className="text-2xl font-bold text-gold">{service.price}</p>
              </div>
            </header>
            
            {/* Service Content */}
            <div className="prose prose-lg max-w-none markdown-content mb-8">
              <ReactMarkdown 
                rehypePlugins={[rehypeRaw]} 
                remarkPlugins={[remarkGfm]}
              >
                {markdownContent}
              </ReactMarkdown>
            </div>
            
            {/* CTA */}
            <div className="bg-cream/30 border border-cream rounded-lg p-6 mt-10">
              <h3 className="text-xl font-serif font-bold text-gray-900 mb-3">
                Tertarik dengan Layanan Ini?
              </h3>
              <p className="text-gray-600 mb-4">
                Tim #PintarPilihKuliah siap membantu Anda. Konsultasikan kebutuhan Anda dengan para ahli kami.
              </p>
              <button
                className="inline-flex items-center justify-center bg-orange-500 hover:bg-black text-white font-medium rounded-md px-6 py-3 transition-colors"
                onClick={handleOrderClick}
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                </svg>
                Pesan Sekarang
              </button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default ServiceDetail;
